import { useTranslation } from 'react-i18next';

import { ColumnDeclaration } from '@/ui/AutoTable';
import { LearnerActions, LearnerInfo, LearnerStatus } from '@/features/journey/admin/atoms/JourneyLearnerTableFormatter';
import { JourneyLearner } from '@/features/journey/admin/types/journeyLearner';
import { SortyBy } from '@/features/journey/admin/hooks/useJourneyLerners';
import { useIsManagementContext } from '@/features/management/manager/hooks/useIsManagementContext';
import { JourneyInsightsParticipantsBulkCheckbox } from '@/features/journey/admin/atoms/JourneyInsightsParticipantsBulkCheckbox';
import { JourneyInsightsParticipantsCheckbox } from '@/features/journey/admin/atoms/JourneyInsightsParticipantsCheckbox/JourneyInsightsParticipantsCheckbox';

export type LearnerRow = JourneyLearner & {
  onRemove?: (ops: { id: number; name: string }) => void;
  selected: boolean;
  onToggleSelected: (learnerId: number) => void;
};

type UseLearnersColumnDeclarationProps = {
  areAllParticipantsSelected: boolean;
  onAllParticipantsSelectToggled: (selected: boolean) => void;
};

export const useLearnersColumnDeclaration = ({
  areAllParticipantsSelected,
  onAllParticipantsSelectToggled,
}: UseLearnersColumnDeclarationProps): ColumnDeclaration<LearnerRow, SortyBy> => {
  const { t } = useTranslation('journey');
  const isManagementContext = useIsManagementContext();

  return [
    ...(isManagementContext
      ? []
      : [
          {
            header: (
              <JourneyInsightsParticipantsBulkCheckbox
                hasSelectedAllParticipants={areAllParticipantsSelected}
                onAllParticipantsIdsSelectedChange={onAllParticipantsSelectToggled}
              />
            ),
            component: JourneyInsightsParticipantsCheckbox,
          },
        ]),
    {
      header: t('edit-learners-table-key-name'),
      component: LearnerInfo,
      sortKey: 'name',
    },
    {
      header: t('edit-learners-table-key-start-date'),
      dataKey: 'startedAt',
      cellFormatter: 'date',
      align: 'right',
      sortKey: 'startedAt',
    },
    {
      header: 'Progress',
      dataKey: 'progress',
      cellFormatter: 'progress',
      align: 'right',
      sortKey: 'progress',
    },
    {
      header: t('edit-learners-table-key-status'),
      component: LearnerStatus,
      sortKey: 'status',
    },
    {
      header: 'Last Activity',
      dataKey: 'lastActivityAt',
      cellFormatter: 'dateToNow',
      align: 'left',
      sortKey: 'lastActivityAt',
    },
    ...(isManagementContext
      ? []
      : ([
          {
            component: LearnerActions,
            align: 'right',
          },
        ] as ColumnDeclaration<LearnerRow, SortyBy>)),
  ];
};
